import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileAlt,
  FaThumbsDown,
  FaThumbsUp,
} from "react-icons/fa";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "../../assets/styles/buyer_cm_quality_approval.scss";

function extractApiErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "Request failed";
}

function unwrapQcEnvelope(res) {
  return res?.data?.data ?? res?.data ?? res;
}

function safeNum(x) {
  const n = Number(String(x ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function fmtYmd(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function asStr(v) {
  return String(v ?? "").trim();
}

/** Parse date-ish value to epoch ms; 0 if invalid. */
function tsMs(raw) {
  if (raw == null || raw === "") return 0;
  const t = new Date(String(raw)).getTime();
  return Number.isFinite(t) ? t : 0;
}

function statusLabel(st) {
  return st === "pass" ? "Passed" : st === "fail" ? "Failed" : "Pending";
}

const CM_EXTERNAL_FLOW = "cm_external_qc";

/** Mirrors `CmExternalQcInspection` — report body lives under `inspection_report.cm_external_qc`. */
function parseExternalInspectionInner(raw) {
  if (raw == null) return null;
  const report = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  if (!report || typeof report !== "object") return null;
  if (report[CM_EXTERNAL_FLOW]) return report[CM_EXTERNAL_FLOW];
  if (report.flow === CM_EXTERNAL_FLOW) return report;
  return null;
}

export default function BuyerCmQualityApprovalPage() {
  const { rfqId: rfqIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rfqId = Number(rfqIdParam);
  const companyId = searchParams.get("companyId");

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  /** Latest `qa_inspections` row for this CM RFQ when buyer recruited external QA (`GET .../qc-requirements`). */
  const [externalInspection, setExternalInspection] = useState(null);
  const [externalInspectionRequired, setExternalInspectionRequired] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  /** Optional comments in "Your decision" — persisted as `buyerQualityApprovalNotes` on approve/reject. */
  const [decisionNotes, setDecisionNotes] = useState("");
  /** Required rejection reason — only the confirm dialog (not the main notes field). */
  const [rejectModalReason, setRejectModalReason] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(rfqId) || rfqId <= 0) {
      toast.error("Invalid RFQ");
      navigate("/dashboard/buyer/contract-manufacturers/quotes");
      return;
    }
    setLoading(true);
    try {
      const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_GET(rfqId));
      const data = unwrapQcEnvelope(res);
      const qc = data?.qc;
      const p = qc?.qc_payload_json ?? qc?.qcPayloadJson ?? null;
      if (!p || typeof p !== "object") {
        toast.error("QC payload not found");
        navigate("/dashboard/buyer/contract-manufacturers/quotes");
        return;
      }
      setPayload(p);
      setDecisionNotes(asStr(p?.buyerQualityApprovalNotes));
      const extReq =
        typeof data?.externalInspectionRequired === "boolean"
          ? data.externalInspectionRequired
          : String(p?.buyerExternalEvaluatorDecision || "").toLowerCase() === "yes";
      setExternalInspectionRequired(extReq);
      setExternalInspection(data?.externalQaInspection ?? null);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
      navigate("/dashboard/buyer/contract-manufacturers/quotes");
    } finally {
      setLoading(false);
    }
  }, [navigate, rfqId]);

  useEffect(() => {
    load();
  }, [load]);

  const report = useMemo(() => {
    const d = payload?.cmInternalInspectionReportData;
    const draft = payload?.cmInternalInspectionReportDraft;
    const snap = (d && typeof d === "object" ? d : null) || (draft && typeof draft === "object" ? draft : null);
    return snap;
  }, [payload]);

  const checklist = useMemo(() => (Array.isArray(report?.checklist) ? report.checklist : []), [report]);

  const qcChecklist = useMemo(() => {
    const raw = payload?.inspectionChecklistItems;
    return Array.isArray(raw) ? raw : [];
  }, [payload]);

  const checklistRows = useMemo(() => {
    if (!Array.isArray(checklist) || checklist.length === 0) return [];
    return checklist.map((c, idx) => {
      const meta = qcChecklist[idx] || null; // align by order index (report stores id/status only)
      const title = asStr(meta?.description) || `Check ${idx + 1}`;
      const criteria = asStr(meta?.acceptanceCriteria) || "—";
      const category = asStr(meta?.category) || "—";
      const st = c?.status;
      return {
        key: String(c?.id ?? idx),
        category,
        title,
        criteria,
        status: st === "pass" || st === "fail" || st === "pending" ? st : "pending",
      };
    });
  }, [checklist, qcChecklist]);

  const complianceStatus = useMemo(() => {
    const cs = report?.complianceStatus;
    return cs && typeof cs === "object" ? cs : {};
  }, [report]);

  const certUploads = useMemo(() => {
    const cu = report?.certificateUploads;
    return cu && typeof cu === "object" ? cu : {};
  }, [report]);

  const internalQcDocs = useMemo(() => {
    const raw = payload?.internalQcDocs;
    return Array.isArray(raw) ? raw : [];
  }, [payload]);

  const externalQcDocs = useMemo(() => {
    const raw = payload?.externalQcDocs;
    return Array.isArray(raw) ? raw : [];
  }, [payload]);

  const extInner = useMemo(
    () => parseExternalInspectionInner(externalInspection?.inspection_report),
    [externalInspection?.inspection_report]
  );

  const externalChecklistRows = useMemo(() => {
    const raw = Array.isArray(extInner?.results) ? extInner.results : [];
    if (raw.length === 0) return [];
    return raw.map((c, idx) => {
      const st = c?.status;
      return {
        key: String(c?.id ?? idx),
        category: asStr(c?.category) || "—",
        title: asStr(c?.title) || `Check ${idx + 1}`,
        criteria: asStr(c?.criteria) || "—",
        status: st === "pass" || st === "fail" || st === "pending" ? st : "pending",
      };
    });
  }, [extInner]);

  const externalComplianceStatus = useMemo(() => {
    /** Same shape as `CmExternalQcInspection` save: `cm_external_qc.compliance.complianceStatus` (not top-level). */
    const nested = extInner?.compliance?.complianceStatus;
    const top = extInner?.complianceStatus;
    const cs =
      nested && typeof nested === "object"
        ? nested
        : top && typeof top === "object"
          ? top
          : null;
    if (cs) return cs;
    const leg = extInner?.compliance?.standards;
    if (!Array.isArray(leg)) return {};
    const ids = ["iso9001", "rohs", "ce", "fcc"];
    return leg.reduce((acc, row, i) => {
      const id = ids[i];
      if (id && row?.status) acc[id] = row.status === "na" ? "pending" : row.status;
      return acc;
    }, {});
  }, [extInner]);

  const extCertUploads = useMemo(() => {
    const cu = extInner?.certificateUploads;
    return cu && typeof cu === "object" ? cu : {};
  }, [extInner]);

  const uploadReports = useMemo(() => {
    const ur = report?.uploadReports;
    return ur && typeof ur === "object" ? ur : null;
  }, [report]);

  const stats = useMemo(() => {
    const total = Array.isArray(checklist) ? checklist.length : 0;
    const passed = checklist.filter((x) => x?.status === "pass").length;
    const failed = checklist.filter((x) => x?.status === "fail").length;
    const pending = checklist.filter((x) => x?.status === "pending").length;
    const passRate = total ? Math.round((passed / total) * 1000) / 10 : 0;
    const defectRate = total ? Math.round((failed / total) * 1000) / 10 : 0;
    return { total, passed, failed, pending, passRate, defectRate };
  }, [checklist]);

  const inspectedQty = useMemo(() => {
    const v = safeNum(report?.batchSize);
    return v ?? safeNum(report?.samplesInspected) ?? null;
  }, [report]);

  const qualityStatus = useMemo(() => {
    if (payload?.buyerInternalInspectionApprovedAt) return "approved";
    if (payload?.buyerInternalInspectionRejectedAt) return "rejected";
    return "pending";
  }, [payload]);

  /** Latest internal approve/reject timestamp (buyer decision on internal QC stored in payload). */
  const internalDecisionMs = useMemo(() => {
    const a = tsMs(payload?.buyerInternalInspectionApprovedAt);
    const r = tsMs(payload?.buyerInternalInspectionRejectedAt);
    return Math.max(a, r);
  }, [payload]);

  /** When external inspection is completed — prefer completed_at, else updated_at. */
  const externalCompletedMs = useMemo(() => {
    if (!externalInspection) return 0;
    if (String(externalInspection.status || "").toLowerCase() !== "completed") return 0;
    const c = tsMs(externalInspection.completed_at ?? externalInspection.completedAt);
    if (c > 0) return c;
    return tsMs(externalInspection.updated_at ?? externalInspection.updatedAt);
  }, [externalInspection]);

  const reportSubmittedAt = String(payload?.cmInternalInspectionReportSubmittedAt || "").trim();
  const internalReportReady = !!reportSubmittedAt;
  const externalInspectionCompleted =
    externalInspection && String(externalInspection.status || "").toLowerCase() === "completed";
  const externalGateOk = !externalInspectionRequired || externalInspectionCompleted;
  const canDecide = internalReportReady && externalGateOk;

  /**
   * Buyer may have approved/rejected internal QC before external QA finished. Same payload fields are reused for
   * "final" QC — unlock Approve/Reject until buyer acts again after external completion.
   */
  const needsFinalQcAfterExternal = useMemo(() => {
    if (!externalInspectionRequired || !externalInspectionCompleted) return false;
    if (!internalDecisionMs || !externalCompletedMs) return false;
    return externalCompletedMs > internalDecisionMs;
  }, [externalInspectionRequired, externalInspectionCompleted, internalDecisionMs, externalCompletedMs]);

  const decisionLocked =
    (qualityStatus === "approved" || qualityStatus === "rejected") && !needsFinalQcAfterExternal;

  /** Status shown in banners (pending while buyer must confirm after external arrived). */
  const displayQualityStatus = needsFinalQcAfterExternal ? "pending" : qualityStatus;

  const extStats = useMemo(() => {
    const raw = Array.isArray(extInner?.results) ? extInner.results : [];
    const total = raw.length;
    const passed = raw.filter((x) => x?.status === "pass").length;
    const failed = raw.filter((x) => x?.status === "fail").length;
    const pending = raw.filter((x) => x?.status === "pending" || !x?.status).length;
    const passRate = total ? Math.round((passed / total) * 1000) / 10 : 0;
    const defectRate = total ? Math.round((failed / total) * 1000) / 10 : 0;
    return { total, passed, failed, pending, passRate, defectRate };
  }, [extInner]);

  const extInspectedQty = useMemo(() => {
    const v = safeNum(extInner?.compliance?.batch_size ?? extInner?.batchSize);
    return v ?? safeNum(extInner?.compliance?.samples_inspected ?? extInner?.samplesInspected) ?? null;
  }, [extInner]);

  async function handleApprove() {
    if (!canDecide || decisionLocked) return;
    try {
      setSubmitting(true);
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_INTERNAL_INSPECTION_APPROVE(rfqId), {
        notes: String(decisionNotes || "").trim(),
      });
      toast.success(needsFinalQcAfterExternal ? "Final QC approved" : "Quality report approved");
      navigate(backHref);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRejectConfirm() {
    const reason = String(rejectModalReason || "").trim();
    if (!reason) {
      setRejectError("Rejection reason is required");
      return;
    }
    try {
      setSubmitting(true);
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.QC_INTERNAL_INSPECTION_REJECT(rfqId), {
        reason,
        notes: String(decisionNotes || "").trim(),
      });
      toast.success(needsFinalQcAfterExternal ? "Final QC rejected" : "Quality report rejected");
      setRejectOpen(false);
      setRejectModalReason("");
      setRejectError("");
      navigate(backHref);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  function handleDownload() {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `internal-qc-report-rfq-${rfqId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadExternal() {
    const raw = externalInspection?.inspection_report;
    if (raw == null) return;
    const blob = new Blob([typeof raw === "string" ? raw : JSON.stringify(raw, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `external-qa-inspection-${externalInspection?.inspection_id ?? rfqId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const backHref = `/dashboard/buyer/contract-manufacturers/quotes${companyId ? `?companyId=${companyId}` : ""}`;
  const productCode = String(payload?.productNameCode || `RFQ-${rfqId}`).trim();

  if (loading) {
    return (
      <div className="qa-approval-page">
        <p className="text-muted">Loading quality approval…</p>
      </div>
    );
  }

  return (
    <div className="qa-approval-page">
      <div className="qa-approval-top">
        <button type="button" className="qa-approval-back" onClick={() => navigate(backHref)}>
          <FaArrowLeft aria-hidden /> Back
        </button>
        <div className="qa-approval-actions">
          <button
            type="button"
            className="qa-btn qa-btn--reject"
            disabled={!canDecide || decisionLocked || submitting}
            onClick={() => {
              setRejectModalReason("");
              setRejectOpen(true);
              setRejectError("");
            }}
          >
            <FaThumbsDown aria-hidden /> Reject QC
          </button>
          <button
            type="button"
            className="qa-btn qa-btn--approve"
            disabled={!canDecide || decisionLocked || submitting}
            onClick={handleApprove}
          >
            <FaThumbsUp aria-hidden /> Approve QC
          </button>
        </div>
      </div>

      <header className="qa-approval-hero">
        <div>
          <h1 className="qa-approval-title">Quality Approval</h1>
          <p className="qa-approval-sub">
            {externalInspectionRequired
              ? "Review the internal and external inspection reports, then approve or reject final QC."
              : "Review the internal QC report and approve or reject the quality inspection."}
          </p>
        </div>
      </header>

      {!internalReportReady ? (
        <div className="qa-alert" role="status">
          <FaExclamationTriangle aria-hidden />
          <div>
            <div className="qa-alert__title">Awaiting internal QC report</div>
            <div className="qa-alert__text">The manufacturer hasn’t submitted the internal inspection report yet.</div>
          </div>
        </div>
      ) : externalInspectionRequired && !externalInspectionCompleted ? (
        <div className="qa-alert" role="status">
          <FaExclamationTriangle aria-hidden />
          <div>
            <div className="qa-alert__title">Awaiting external QA inspection</div>
            <div className="qa-alert__text">
              You selected third-party QA for this RFQ. Final approve/reject is available after the inspector completes and
              submits the external inspection report.
            </div>
          </div>
        </div>
      ) : (
        <div className={`qa-status qa-status--${displayQualityStatus}`} role="status">
          {displayQualityStatus === "approved" ? (
            <>
              <FaCheckCircle aria-hidden /> Approved
            </>
          ) : displayQualityStatus === "rejected" ? (
            <>
              <FaExclamationTriangle aria-hidden /> Rejected
            </>
          ) : (
            <>
              <FaExclamationTriangle aria-hidden /> Awaiting buyer approval
            </>
          )}
          {needsFinalQcAfterExternal ? (
            <span className="qa-status__reason">
              External inspection was completed after your previous decision. Review both reports and confirm final QC
              below.
            </span>
          ) : null}
          {!needsFinalQcAfterExternal &&
          payload?.buyerInternalInspectionRejectedAt &&
          payload?.buyerInternalInspectionRejectionReason ? (
            <span className="qa-status__reason">
              Reason: {String(payload.buyerInternalInspectionRejectionReason).trim()}
            </span>
          ) : null}
          {asStr(payload?.buyerQualityApprovalNotes) ? (
            <span className="qa-status__reason">Your notes: {asStr(payload.buyerQualityApprovalNotes)}</span>
          ) : null}
        </div>
      )}

      <main className="qa-approval-grid">
        <div className="qa-approval-main-col">
        <section className="qa-card qa-card--wide">
          <div className="qa-card-head">
            <div className="qa-card-title">
              Internal QC Report {internalReportReady ? <span className="qa-pill qa-pill--done">Completed</span> : null}
            </div>
            <div className="qa-card-actions">
              <button type="button" className="qa-link" onClick={handleDownload} disabled={!report}>
                <FaDownload aria-hidden /> Download
              </button>
            </div>
          </div>

          <div className="qa-metrics">
            <div className="qa-metric">
              <div className="qa-metric__label">Pass rate</div>
              <div className="qa-metric__value">{stats.passRate}%</div>
            </div>
            <div className="qa-metric">
              <div className="qa-metric__label">Defect rate</div>
              <div className="qa-metric__value">{stats.defectRate}%</div>
            </div>
            <div className="qa-metric">
              <div className="qa-metric__label">Inspected</div>
              <div className="qa-metric__value">{inspectedQty ?? "—"}</div>
            </div>
            <div className="qa-metric">
              <div className="qa-metric__label">Failed</div>
              <div className="qa-metric__value">{stats.failed}</div>
            </div>
          </div>

          <div className="qa-findings">
            <div className="qa-findings__title">Key findings</div>
            {checklist.length === 0 ? (
              <div className="qa-muted">No checklist data found in the report.</div>
            ) : (
              <ul className="qa-findings__list">
                {checklist.slice(0, 6).map((c) => {
                  const st = c?.status;
                  const label = st === "pass" ? "Passed" : st === "fail" ? "Failed" : "Pending";
                  const idx = checklist.indexOf(c);
                  const meta = qcChecklist[idx] || null;
                  const desc = asStr(meta?.description);
                  return (
                    <li key={String(c?.id || Math.random())} className={`qa-finding qa-finding--${st || "pending"}`}>
                      <span className="qa-finding__dot" aria-hidden />
                      <span className="qa-finding__text">
                        {desc ? `${desc} — ` : ""}
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="qa-report-meta">
            <div>Inspection date: {fmtYmd(report?.inspectionDate)}</div>
            <div>Lead inspector: {asStr(report?.inspectorName) || "—"}</div>
            <div>Product: {productCode}</div>
          </div>

          {report ? (
            <div className="qa-details">
              <div className="qa-details__title">Internal report details</div>

              <div className="qa-details-grid">
                <div className="qa-details-card">
                  <div className="qa-details-card__head">Inspection information</div>
                  <div className="qa-kv">
                    <div className="qa-kv__row">
                      <div className="qa-kv__k">Facility / team</div>
                      <div className="qa-kv__v">{asStr(report?.facilityName) || "—"}</div>
                    </div>
                    <div className="qa-kv__row">
                      <div className="qa-kv__k">Inspector</div>
                      <div className="qa-kv__v">{asStr(report?.inspectorName) || "—"}</div>
                    </div>
                    <div className="qa-kv__row">
                      <div className="qa-kv__k">Inspection date</div>
                      <div className="qa-kv__v">{fmtYmd(report?.inspectionDate)}</div>
                    </div>
                    <div className="qa-kv__row">
                      <div className="qa-kv__k">Batch size</div>
                      <div className="qa-kv__v">{asStr(report?.batchSize) || "—"}</div>
                    </div>
                    <div className="qa-kv__row">
                      <div className="qa-kv__k">Samples inspected</div>
                      <div className="qa-kv__v">{asStr(report?.samplesInspected) || "—"}</div>
                    </div>
                    <div className="qa-kv__row">
                      <div className="qa-kv__k">AQL applied</div>
                      <div className="qa-kv__v">{asStr(report?.aqlApplied) || "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="qa-details-card">
                  <div className="qa-details-card__head">Compliance standards tested</div>
                  <div className="qa-kv">
                    {[
                      { id: "iso9001", label: "ISO 9001:2015" },
                      { id: "rohs", label: "RoHS Compliance" },
                      { id: "ce", label: "CE Marking" },
                      { id: "fcc", label: "FCC Certification" },
                    ].map((s) => (
                      <div key={s.id} className="qa-kv__row">
                        <div className="qa-kv__k">{s.label}</div>
                        <div className={`qa-kv__v qa-pill-status qa-pill-status--${String(complianceStatus?.[s.id] || "pending")}`}>
                          {statusLabel(complianceStatus?.[s.id])}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="qa-details-card qa-details-card--wide">
                <div className="qa-details-card__head">Lab test results</div>
                <div className="qa-pre">{asStr(report?.labTestResults) || "—"}</div>
              </div>

              <div className="qa-details-card qa-details-card--wide">
                <div className="qa-details-card__head">Inspection results</div>
                {checklistRows.length === 0 ? (
                  <div className="qa-muted">No inspection results found.</div>
                ) : (
                  <div className="qa-table">
                    <div className="qa-table__head">
                      <div>Category</div>
                      <div>Check</div>
                      <div>Status</div>
                    </div>
                    {checklistRows.map((r) => (
                      <div key={r.key} className="qa-table__row">
                        <div className="qa-table__cell qa-muted">{asStr(r.category) || "—"}</div>
                        <div className="qa-table__cell">
                          <div className="qa-table__title">{r.title}</div>
                          <div className="qa-table__sub qa-muted">Acceptance: {r.criteria}</div>
                        </div>
                        <div className={`qa-table__cell qa-pill-status qa-pill-status--${r.status}`}>
                          {statusLabel(r.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="qa-details-grid">
                <div className="qa-details-card">
                  <div className="qa-details-card__head">Certificates (Internal QC docs)</div>
                  {internalQcDocs.length === 0 ? (
                    <div className="qa-muted">No internal QC document requirements defined.</div>
                  ) : (
                    <ul className="qa-file-list">
                      {internalQcDocs.map((d, idx) => {
                        const id = String(d?.id ?? `iqc-${idx}`);
                        const name = asStr(d?.name) || `Document ${idx + 1}`;
                        const req = d?.required !== false;
                        const uploaded = asStr(certUploads?.[id]?.fileName);
                        return (
                          <li key={id} className="qa-file">
                            <div>
                              <div className="qa-file__name">
                                {name} {req ? <span className="qa-pill qa-pill--req">Required</span> : <span className="qa-pill">Optional</span>}
                              </div>
                              <div className="qa-muted">Format: {asStr(d?.fileFormat).toUpperCase() || "—"}</div>
                            </div>
                            <div className={`qa-file__right ${uploaded ? "" : "qa-muted"}`}>{uploaded || "Not attached"}</div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="qa-details-card">
                  <div className="qa-details-card__head">Upload reports</div>
                  {!uploadReports ? (
                    <div className="qa-muted">No upload report metadata found.</div>
                  ) : (
                    <ul className="qa-file-list">
                      <li className="qa-file">
                        <div className="qa-file__name">Comprehensive report</div>
                        <div className={`qa-file__right ${asStr(uploadReports?.comprehensive?.fileName) ? "" : "qa-muted"}`}>
                          {asStr(uploadReports?.comprehensive?.fileName) || "Not attached"}
                        </div>
                      </li>
                      <li className="qa-file">
                        <div className="qa-file__name">Photo documentation</div>
                        <div className={`qa-file__right ${Array.isArray(uploadReports?.photos?.fileNames) && uploadReports.photos.fileNames.length ? "" : "qa-muted"}`}>
                          {Array.isArray(uploadReports?.photos?.fileNames) && uploadReports.photos.fileNames.length
                            ? `${uploadReports.photos.fileNames.length} file(s)`
                            : "Not attached"}
                        </div>
                      </li>
                      <li className="qa-file">
                        <div className="qa-file__name">Measurement &amp; test data</div>
                        <div className={`qa-file__right ${asStr(uploadReports?.measurement?.fileName) ? "" : "qa-muted"}`}>
                          {asStr(uploadReports?.measurement?.fileName) || "Not attached"}
                        </div>
                      </li>
                      <li className="qa-file">
                        <div className="qa-file__name">Non-conformance report</div>
                        <div className={`qa-file__right ${asStr(uploadReports?.nonConformance?.fileName) ? "" : "qa-muted"}`}>
                          {asStr(uploadReports?.nonConformance?.fileName) || "Not attached"}
                        </div>
                      </li>
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </section>

        {externalInspectionRequired ? (
          <section className="qa-card qa-card--wide">
            <div className="qa-card-head">
              <div className="qa-card-title">
                External QA inspection report{" "}
                {externalInspectionCompleted ? (
                  <span className="qa-pill qa-pill--done">Completed</span>
                ) : externalInspection ? (
                  <span className="qa-pill">In progress</span>
                ) : (
                  <span className="qa-pill">Not started</span>
                )}
              </div>
              <div className="qa-card-actions">
                <button
                  type="button"
                  className="qa-link"
                  onClick={handleDownloadExternal}
                  disabled={!externalInspection?.inspection_report}
                >
                  <FaDownload aria-hidden /> Download
                </button>
              </div>
            </div>

            {!externalInspection ? (
              <div className="qa-muted">
                Third-party QA is required for this RFQ, but no inspection record is linked yet (for example, before an
                inspector is assigned).
              </div>
            ) : !externalInspectionCompleted ? (
              <div className="qa-muted">
                Current inspector status: <strong>{asStr(externalInspection.status) || "—"}</strong>
                {externalInspection.completed_at ? (
                  <>
                    {" "}
                    · Completed at {fmtYmd(externalInspection.completed_at)}
                  </>
                ) : null}
                . Full report details will appear here after the inspector submits.
              </div>
            ) : (
              <>
                <div className="qa-metrics">
                  <div className="qa-metric">
                    <div className="qa-metric__label">Pass rate</div>
                    <div className="qa-metric__value">{extStats.passRate}%</div>
                  </div>
                  <div className="qa-metric">
                    <div className="qa-metric__label">Defect rate</div>
                    <div className="qa-metric__value">{extStats.defectRate}%</div>
                  </div>
                  <div className="qa-metric">
                    <div className="qa-metric__label">Inspected</div>
                    <div className="qa-metric__value">{extInspectedQty ?? "—"}</div>
                  </div>
                  <div className="qa-metric">
                    <div className="qa-metric__label">Failed</div>
                    <div className="qa-metric__value">{extStats.failed}</div>
                  </div>
                </div>

                <div className="qa-report-meta">
                  <div>
                    Inspection date:{" "}
                    {fmtYmd(extInner?.agency?.inspectionDate || externalInspection?.inspection_date)}
                  </div>
                  <div>
                    Inspector: {asStr(extInner?.agency?.inspectorName) || "—"} · Agency:{" "}
                    {asStr(extInner?.agency?.name) || "—"}
                  </div>
                  <div>Product: {productCode}</div>
                </div>

                <div className="qa-details">
                  <div className="qa-details__title">External report details</div>

                  <div className="qa-details-grid">
                    <div className="qa-details-card">
                      <div className="qa-details-card__head">Sampling &amp; compliance</div>
                      <div className="qa-kv">
                        <div className="qa-kv__row">
                          <div className="qa-kv__k">Batch size</div>
                          <div className="qa-kv__v">
                            {asStr(extInner?.compliance?.batch_size ?? extInner?.batchSize) || "—"}
                          </div>
                        </div>
                        <div className="qa-kv__row">
                          <div className="qa-kv__k">Samples inspected</div>
                          <div className="qa-kv__v">
                            {asStr(extInner?.compliance?.samples_inspected ?? extInner?.samplesInspected) || "—"}
                          </div>
                        </div>
                        <div className="qa-kv__row">
                          <div className="qa-kv__k">AQL applied</div>
                          <div className="qa-kv__v">
                            {asStr(extInner?.compliance?.aqlApplied ?? extInner?.compliance?.aql) || "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="qa-details-card">
                      <div className="qa-details-card__head">Compliance standards tested</div>
                      <div className="qa-kv">
                        {[
                          { id: "iso9001", label: "ISO 9001:2015" },
                          { id: "rohs", label: "RoHS Compliance" },
                          { id: "ce", label: "CE Marking" },
                          { id: "fcc", label: "FCC Certification" },
                        ].map((s) => (
                          <div key={s.id} className="qa-kv__row">
                            <div className="qa-kv__k">{s.label}</div>
                            <div
                              className={`qa-kv__v qa-pill-status qa-pill-status--${String(
                                externalComplianceStatus?.[s.id] || "pending"
                              )}`}
                            >
                              {statusLabel(externalComplianceStatus?.[s.id])}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="qa-details-card qa-details-card--wide">
                    <div className="qa-details-card__head">Lab / compliance notes</div>
                    <div className="qa-pre">
                      {asStr(extInner?.compliance_lab_notes ?? extInner?.labTestResults) || "—"}
                    </div>
                  </div>

                  <div className="qa-details-card qa-details-card--wide">
                    <div className="qa-details-card__head">Inspection results</div>
                    {externalChecklistRows.length === 0 ? (
                      <div className="qa-muted">No checklist results in the external report payload.</div>
                    ) : (
                      <div className="qa-table">
                        <div className="qa-table__head">
                          <div>Category</div>
                          <div>Check</div>
                          <div>Status</div>
                        </div>
                        {externalChecklistRows.map((r) => (
                          <div key={r.key} className="qa-table__row">
                            <div className="qa-table__cell qa-muted">{asStr(r.category) || "—"}</div>
                            <div className="qa-table__cell">
                              <div className="qa-table__title">{r.title}</div>
                              <div className="qa-table__sub qa-muted">Acceptance: {r.criteria}</div>
                            </div>
                            <div className={`qa-table__cell qa-pill-status qa-pill-status--${r.status}`}>
                              {statusLabel(r.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="qa-details-grid">
                    <div className="qa-details-card">
                      <div className="qa-details-card__head">Certificates (External QC docs)</div>
                      {externalQcDocs.length === 0 ? (
                        <div className="qa-muted">No external QC document requirements defined in the agreed payload.</div>
                      ) : (
                        <ul className="qa-file-list">
                          {externalQcDocs.map((d, idx) => {
                            const id = String(d?.id ?? `eqc-${idx}`);
                            const name = asStr(d?.name) || `Document ${idx + 1}`;
                            const req = d?.required !== false;
                            const uploaded = asStr(extCertUploads?.[id]?.fileName);
                            return (
                              <li key={id} className="qa-file">
                                <div>
                                  <div className="qa-file__name">
                                    {name}{" "}
                                    {req ? (
                                      <span className="qa-pill qa-pill--req">Required</span>
                                    ) : (
                                      <span className="qa-pill">Optional</span>
                                    )}
                                  </div>
                                  <div className="qa-muted">Format: {asStr(d?.fileFormat).toUpperCase() || "—"}</div>
                                </div>
                                <div className={`qa-file__right ${uploaded ? "" : "qa-muted"}`}>
                                  {uploaded || "Not attached"}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    <div className="qa-details-card">
                      <div className="qa-details-card__head">Inspector uploads</div>
                      {extInner?.uploads && typeof extInner.uploads === "object" ? (
                        <ul className="qa-file-list">
                          {[
                            { k: "comprehensive_report", label: "Comprehensive report" },
                            { k: "photo_docs", label: "Photo documentation" },
                            { k: "measurement_data", label: "Measurement & test data" },
                            { k: "non_conformance", label: "Non-conformance" },
                          ].map(({ k, label }) => {
                            const v = extInner.uploads[k];
                            const path = typeof v === "string" ? v : v && typeof v === "object" ? v.path || v.filePath : "";
                            const name =
                              (typeof v === "object" && v?.fileName) ||
                              (path ? String(path).split("/").pop() : "");
                            return (
                              <li key={k} className="qa-file">
                                <div className="qa-file__name">{label}</div>
                                <div className={`qa-file__right ${name ? "" : "qa-muted"}`}>{name || "Not attached"}</div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="qa-muted">No upload metadata in the external report.</div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        ) : null}

        </div>

        <aside className="qa-side">
          <section className="qa-card">
            <div className="qa-card-title">Approval status</div>
            <div className="qa-side-status">
              <div className="qa-side-status__label">Current status</div>
              <div className={`qa-side-status__pill qa-side-status__pill--${displayQualityStatus}`}>
                {displayQualityStatus === "approved"
                  ? "Approved"
                  : displayQualityStatus === "rejected"
                    ? "Rejected"
                    : needsFinalQcAfterExternal
                      ? "Confirm final QC"
                      : "Awaiting buyer decision"}
              </div>
              <div className="qa-muted">Approve or reject to proceed.</div>
            </div>
          </section>

          <section className="qa-card">
            <div className="qa-card-title">Quality summary</div>
            <div className="qa-summary">
              <div className="qa-summary__big">{stats.passRate}%</div>
              <div className="qa-muted">Internal pass rate{externalInspectionCompleted ? ` · External ${extStats.passRate}%` : ""}</div>
              <div className="qa-summary__items">
                <div className="qa-summary__item">
                  <FaFileAlt aria-hidden /> Internal QC — {internalReportReady ? "submitted" : "pending"}
                </div>
                {externalInspectionRequired ? (
                  <div className="qa-summary__item">
                    <FaFileAlt aria-hidden /> External QA — {externalInspectionCompleted ? "completed" : "pending"}
                  </div>
                ) : (
                  <div className="qa-summary__item qa-muted">
                    <FaFileAlt aria-hidden /> External QA — not required for this RFQ
                  </div>
                )}
                <div className="qa-summary__item">
                  <FaFileAlt aria-hidden /> Inspection results (see reports)
                </div>
                <div className="qa-summary__item">
                  <FaFileAlt aria-hidden /> Compliance certificates
                </div>
              </div>
            </div>
          </section>

          <section className="qa-card">
            <div className="qa-card-title">Contact information</div>
            <div className="qa-muted">
              Use the Quotes page to contact the manufacturer for clarifications.
            </div>
            <div style={{ marginTop: 10 }}>
              <Link className="qa-link" to={backHref}>
                Back to quotes
              </Link>
            </div>
          </section>
        </aside>
      </main>

      <section className="qa-decision">
        <div className="qa-card qa-card--wide">
          <div className="qa-card-title">Your decision</div>
          <textarea
            className="qa-decision__notes"
            rows={4}
            placeholder="Add any comments or notes regarding this quality approval decision…"
            value={decisionNotes}
            onChange={(e) => setDecisionNotes(e.target.value)}
            disabled={decisionLocked || submitting}
          />
          <div className="qa-decision__actions">
            <button
              type="button"
              className="qa-btn qa-btn--reject"
              disabled={!canDecide || decisionLocked || submitting}
              onClick={() => {
                setRejectModalReason("");
                setRejectOpen(true);
                setRejectError("");
              }}
            >
              <FaThumbsDown aria-hidden /> Reject quality
            </button>
            <button
              type="button"
              className="qa-btn qa-btn--approve"
              disabled={!canDecide || decisionLocked || submitting}
              onClick={handleApprove}
            >
              <FaThumbsUp aria-hidden /> Approve quality
            </button>
          </div>
        </div>
      </section>

      {rejectOpen ? (
        <div className="qa-reject-backdrop" onClick={() => setRejectOpen(false)} role="presentation">
          <div className="qa-reject-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="qa-reject-title">Reject Quality Report?</div>
            <div className="qa-reject-sub">
              Please provide a reason for rejecting this quality report. The manufacturer will be notified and may need to take corrective actions.
            </div>
            <textarea
              className={`qa-reject-textarea${rejectError ? " qa-reject-textarea--error" : ""}`}
              rows={4}
              value={rejectModalReason}
              onChange={(e) => {
                setRejectModalReason(e.target.value);
                if (rejectError) setRejectError("");
              }}
              placeholder="Describe the quality concerns or issues found..."
              disabled={submitting}
            />
            {rejectError ? <div className="qa-reject-error">{rejectError}</div> : null}
            <div className="qa-reject-actions">
              <button type="button" className="qa-btn qa-btn--ghost" onClick={() => setRejectOpen(false)} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="qa-btn qa-btn--reject" onClick={handleRejectConfirm} disabled={submitting}>
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

